/**
 * The only waiting logic the frontend has.
 *
 * The sentence that explains a wait is written by the server and rendered
 * verbatim (`docs/API.md`, *Waiting, explained*): the console knows the lane,
 * the machine being started for its head and the reason no machine could be
 * started, and this bundle knows none of the three. What is left here is the
 * arithmetic of a screen — an ordinal, a countdown to an estimate the server
 * published, and the rows of a list — and none of it invents a fact.
 *
 * Every function is pure and takes `now`, so nothing here needs a timer: a
 * waiting workspace page re-renders every 3 s and the home page every 5 s,
 * both finer than the minute resolution these strings show.
 */

import type { QueueEntry, Waiting, WaitingMachine } from "../api/types";
import { formatDuration, formatElapsed, pluralize, remainingUntil } from "./format";

/** The unit a lane is counted in: GPUs on one fleet, cores on the other. */
export type WaitingUnit = "GPU" | "core";

/**
 * One row of a queue, whether it came from `capacity.queue` or from the
 * `ahead` list on a request's own `waiting` object.
 *
 * `key` rather than `id`: an `ahead` entry carries no id — the payload names
 * who is ahead and how big their request is, and nothing else — so a row built
 * from one has an index for a key and never pretends to identify an object.
 */
export interface WaitingRow {
  key: string;
  position: number;
  user: string;
  gpus: number | null;
  cores: number | null;
  asked_at: string;
  /** The console's clause about this request; empty when it published none. */
  note: string;
}

/** Who a queue row is compared against to be marked as yours. */
export interface WaitingSelf {
  id: string;
  user: string;
  gpus: number | null;
  cores: number | null;
}

/**
 * The ordinal of a place in a line: `1st`, `2nd`, `3rd`, `4th`, `11th`.
 *
 * The teens are the whole reason this is a function rather than a lookup on
 * the last digit — `11st` is how a queue stops being believable.
 *
 * Args:
 *     position: A 1-based place in the queue.
 *
 * Returns:
 *     The place as an English ordinal.
 */
export function ordinal(position: number): string {
  const last = position % 10;
  const teen = position % 100;
  if (teen >= 11 && teen <= 13) {
    return `${position}th`;
  }
  if (last === 1) {
    return `${position}st`;
  }
  if (last === 2) {
    return `${position}nd`;
  }
  if (last === 3) {
    return `${position}rd`;
  }
  return `${position}th`;
}

/** The machine clause: how long it has been coming, and how long is left. */
function machineClause(machine: WaitingMachine | null, now: number): string {
  if (machine === null) {
    return "No machine is being started.";
  }
  const since = formatElapsed(machine.requested_at, now);
  if (machine.late) {
    // The server's verdict, never recomputed here — and no countdown past an
    // estimate the machine has already missed.
    return (
      `The machine asked for ${since} ago has not joined; the console keeps ` +
      `checking.`
    );
  }
  if (machine.eta_at === null) {
    return (
      `Machine asked for ${since} ago. How long a machine takes to start has ` +
      `never been measured here, so the console does not say.`
    );
  }
  const left = remainingUntil(machine.eta_at, now);
  if (left === null) {
    return `Machine asked for ${since} ago.`;
  }
  return left > 0
    ? `Machine asked for ${since} ago — about ${formatDuration(left)} left.`
    : `Machine asked for ${since} ago — it is due now.`;
}

/**
 * The live line under a waiting request's headline.
 *
 * Up to three clauses — where you stand, what the machine is doing, what is
 * free — none of which restates the headline the server wrote. The free clause
 * says **on one machine** because that is what the number is: the largest free
 * block on a single machine, and for a pinned request the block on that one
 * machine — never the fleet-wide count the capacity section on the same screen
 * publishes. At zero it keeps exactly that scope: a pinned request whose own
 * machine is full says nothing about the machine standing idle beside it.
 *
 * Args:
 *     waiting: The wait, as the server published it.
 *     unit: What this lane is counted in.
 *     now: Reference instant in milliseconds (injectable for tests).
 *
 * Returns:
 *     One line, the clauses joined by a middle dot.
 */
export function waitingNote(
  waiting: Waiting,
  unit: WaitingUnit,
  now: number = Date.now(),
): string {
  const place =
    waiting.position === 1
      ? "You are next in line."
      : `${pluralize(waiting.position - 1, "request")} ahead of yours.`;
  const clauses = [place, machineClause(waiting.machine, now)];
  if (waiting.free_now !== null) {
    clauses.push(
      waiting.free_now === 0
        ? `No ${unit} is free on a machine this request can use right now.`
        : `Room for ${pluralize(waiting.free_now, unit)} on one machine right now.`,
    );
  }
  return clauses.join(" · ");
}

/**
 * The one line a card carries for a workspace that is still waiting.
 *
 * The place is this side's arithmetic; the clause after it is the console's
 * own and is not touched.
 *
 * Args:
 *     waiting: The wait, as the server published it.
 *
 * Returns:
 *     e.g. `2nd in line · machine starting, ~3 min`.
 */
export function waitingCardLine(waiting: Waiting): string {
  return `${ordinal(waiting.position)} in line · ${waiting.note}`;
}

/**
 * The published queue as list rows.
 *
 * Args:
 *     entries: `capacity.queue`, or `capacity.cpu.queue`.
 *
 * Returns:
 *     One row per entry, in the order they arrived — the order they are served.
 */
export function rowsFromQueue(entries: QueueEntry[]): WaitingRow[] {
  return entries.map((entry) => ({
    key: entry.id,
    position: entry.position,
    user: entry.user,
    gpus: entry.gpus,
    cores: entry.cores,
    asked_at: entry.asked_at,
    note: entry.note,
  }));
}

/**
 * The line one request stands in, as its own `waiting` object describes it:
 * everybody ahead, then you.
 *
 * Only your row carries a clause — the payload publishes no note for the
 * requests ahead, deliberately: what the request at the head is waiting for is
 * already said in your own sentence, and inventing a clause for somebody
 * else's request here would be this bundle authoring a wait.
 *
 * Args:
 *     waiting: The wait, as the server published it.
 *     self: The request being looked at.
 *
 * Returns:
 *     `position` rows, oldest first, yours last.
 */
export function rowsFromWaiting(
  waiting: Waiting,
  self: WaitingSelf,
): WaitingRow[] {
  const ahead = waiting.ahead.map((row, index) => ({
    key: `ahead-${index}`,
    position: index + 1,
    user: row.user,
    gpus: row.gpus,
    cores: row.cores,
    asked_at: row.asked_at,
    note: "",
  }));
  return [
    ...ahead,
    {
      key: self.id,
      position: waiting.position,
      user: self.user,
      gpus: self.gpus,
      cores: self.cores,
      asked_at: waiting.asked_at,
      note: waiting.note,
    },
  ];
}
